import { useState } from "react";
import { Eye, Loader2, Inbox } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtAmount, fmtDate } from "@/lib/render";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { RefundButton } from "@/components/processor-engine/RefundDialog";

const KIND_STYLE = {
  deposit: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30",
  withdrawal: "bg-red-500/15 text-red-600 dark:text-red-300 border-red-500/30",
  charge: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 border-indigo-500/30",
  refund: "bg-amber-500/15 text-amber-600 dark:text-amber-300 border-amber-500/30",
};
const STATUS_STYLE = {
  approved: "text-emerald-500", partially_refunded: "text-amber-500", refunded: "text-muted-foreground", reversed: "text-red-500",
};
const refundable = (r) => ["deposit", "charge"].includes(r.kind) && r.method === "card" && ["approved", "partially_refunded"].includes(r.status);

const Reveal = ({ rec }) => {
  const [val, setVal] = useState(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/processor-engine/records/${rec.id}/reveal`); setVal(data.value); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (val) return <code data-testid="pe-revealed-value" className="font-mono text-xs">{val}</code>;
  return (
    <Button variant="ghost" size="sm" onClick={go} disabled={busy} data-testid="pe-reveal-button" className="h-7 text-xs text-muted-foreground">
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Eye className="h-3 w-3 mr-1" />Reveal</>}
    </Button>
  );
};

export const EngineRecords = ({ rows, isAdmin, onChanged }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden">
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-border hover:bg-transparent">
            {["Type", "Status", "Name", "Instrument", "Amount", "Source", "TXHS", "Time", isAdmin ? "Actions" : null].filter(Boolean).map((h) => (
              <TableHead key={h} className="text-muted-foreground text-xs uppercase tracking-wider">{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows === null && <TableRow><TableCell colSpan={9} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>}
          {rows?.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground"><Inbox className="h-8 w-8 mx-auto mb-2 opacity-50" />No processor activity yet</TableCell></TableRow>}
          {rows?.map((r) => (
            <TableRow key={r.id} data-testid="pe-record-row" className="border-border hover:bg-accent/60 transition-colors">
              <TableCell><Badge className={`capitalize ${KIND_STYLE[r.kind]}`}>{r.kind}</Badge></TableCell>
              <TableCell data-testid="pe-record-status" className={`text-xs capitalize ${STATUS_STYLE[r.status] || ""}`}>
                {r.status.replace("_", " ")}{r.refunded_amount ? ` · ${fmtAmount(r.refunded_amount, "USD")}` : ""}
              </TableCell>
              <TableCell className="text-sm">{r.depositor?.full_name}</TableCell>
              <TableCell className="font-mono text-xs">{r.instrument}{r.card?.expiry ? ` · ${r.card.expiry}` : ""}</TableCell>
              <TableCell className={`font-mono text-sm ${["withdrawal", "refund"].includes(r.kind) ? "text-red-500" : "text-emerald-500"}`}>
                {["withdrawal", "refund"].includes(r.kind) ? "−" : "+"}{fmtAmount(r.amount, r.currency)}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">{r.source === "vault_api" ? "Vault API" : "Engine form"}</TableCell>
              <TableCell className="font-mono text-xs" title={r.tx_hash}>{r.tx_hash?.slice(0, 14)}…</TableCell>
              <TableCell className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
              {isAdmin && (
                <TableCell className="whitespace-nowrap">
                  {r.kind !== "refund" && <Reveal rec={r} />}
                  {refundable(r) && <RefundButton rec={r} onDone={onChanged} />}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
);
